/**
 * ModeToggle (web) — Annie Mode Phase 8 (PR #152).
 *
 * Mirrors apps/mobile/src/components/ModeToggle.tsx. Tap on inactive mode:
 *   - blocked → annieReason notice
 *   - ready + no open positions in current mode → optimistic flip
 *   - ready + open positions → confirmation dialog
 *
 * Optimistic flip writes via authService.updateActiveMode + sets user state.
 * Web uses native <dialog> for the confirmation (simpler than custom modal).
 */

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { modePalettes, type ActiveMode } from '@trading-app/shared';
import { tradingService } from '../services/trading';
import { useAuth } from '../contexts/AuthContext';
import { useModeTheme } from '../contexts/ModeThemeContext';

export function ModeToggle() {
  const { user, updateActiveMode } = useAuth();
  const { mode: currentMode } = useModeTheme();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<ActiveMode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const readinessQuery = useQuery({
    queryKey: ['mode-readiness'],
    queryFn: () => tradingService.getModeReadiness(),
    enabled: !!user,
    staleTime: 30 * 1000,
  });

  const openAggregatesQuery = useQuery({
    queryKey: ['trade-aggregates', 'open'],
    queryFn: () => tradingService.getOpenAggregates(),
    enabled: !!user,
    staleTime: 15 * 1000,
  });

  const performFlip = async (target: ActiveMode) => {
    // Codex #152 F1 fix: route through AuthContext.updateActiveMode so the
    // optimistic write updates user.activeMode (the source of truth for
    // ModeThemeBinder). Without this, the binder reverts the theme on its
    // next re-render after the PATCH succeeds. AuthContext handles the
    // rollback on failure.
    setNotice(null);
    try {
      await updateActiveMode(target);
      queryClient.invalidateQueries({ queryKey: ['mode-readiness'] });
      queryClient.invalidateQueries({ queryKey: ['trade-aggregates'] });
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Failed to switch mode');
    }
  };

  const onTap = (target: ActiveMode) => {
    if (target === currentMode) return;
    if (target === 'ANNIE' && readinessQuery.data && !readinessQuery.data.annieReady) {
      setNotice(readinessQuery.data.annieReason ?? 'Annie is not ready yet.');
      return;
    }
    const currentModeOpenCount = (openAggregatesQuery.data ?? []).filter(
      (agg) => agg.status === 'OPEN' && agg.algorithm === currentMode,
    ).length;
    if (currentModeOpenCount === 0) {
      void performFlip(target);
      return;
    }
    setPending(target);
    dialogRef.current?.showModal();
  };

  const onConfirm = async () => {
    const target = pending;
    setPending(null);
    dialogRef.current?.close();
    if (target) await performFlip(target);
  };

  const onCancel = () => {
    setPending(null);
    dialogRef.current?.close();
  };

  const currentName = currentMode === 'ANNIE' ? 'Annie' : 'Eric';
  const pendingName = pending === 'ANNIE' ? 'Annie' : 'Eric';
  const currentModeOpenCount = (openAggregatesQuery.data ?? []).filter(
    (agg) => agg.status === 'OPEN' && agg.algorithm === currentMode,
  ).length;

  return (
    <div>
      <div className="text-text-gray text-xs uppercase tracking-wider mb-2">Mode</div>
      <div className="flex gap-3">
        <ModeCard
          mode="ERIC"
          isActive={currentMode === 'ERIC'}
          isReady={readinessQuery.data?.ericReady ?? true}
          onClick={() => onTap('ERIC')}
        />
        <ModeCard
          mode="ANNIE"
          isActive={currentMode === 'ANNIE'}
          isReady={readinessQuery.data?.annieReady ?? false}
          onClick={() => onTap('ANNIE')}
        />
      </div>
      {notice && (
        <div className="mt-2 px-3 py-2 rounded text-xs text-rh-red border border-rh-red/40 bg-rh-red/5">
          {notice}
        </div>
      )}
      <dialog ref={dialogRef} className="rounded-2xl p-6 max-w-md backdrop:bg-black/50">
        <h2 className="text-xl font-semibold mb-3">Switch to {pendingName}?</h2>
        <p className="text-text-gray text-base mb-6">
          {currentModeOpenCount === 1
            ? `1 open ${currentName} position will continue to be managed under ${currentName}'s rules until it closes naturally.`
            : `${currentModeOpenCount} open ${currentName} positions will continue to be managed under ${currentName}'s rules until they close naturally.`}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 h-10 rounded-full bg-brand-primary text-white text-base font-semibold active:opacity-80"
          >
            Switch to {pendingName}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-10 rounded-full border border-border-gray text-base font-semibold active:opacity-70"
          >
            Cancel
          </button>
        </div>
      </dialog>
    </div>
  );
}

function ModeCard({
  mode,
  isActive,
  isReady,
  onClick,
}: {
  mode: ActiveMode;
  isActive: boolean;
  isReady: boolean;
  onClick: () => void;
}) {
  const palette = modePalettes[mode];
  const isDimmed = !isActive && !isReady;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      aria-disabled={isDimmed}
      className={`flex-1 rounded-2xl p-4 text-left transition-opacity ${isActive ? '' : 'opacity-60'} ${isDimmed ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{
        backgroundColor: isActive ? palette.brandPrimary : 'transparent',
        border: isActive ? 'none' : '1px solid #d9d9d9',
      }}
    >
      <div
        className="text-base font-semibold"
        style={{
          color: isActive ? '#fff' : '#333',
          fontFamily: palette.wordmark.family,
          fontStyle: palette.wordmark.style,
          fontWeight: palette.wordmark.weight,
        }}
      >
        {palette.name}
      </div>
      <div className="text-xs mt-1" style={{ color: isActive ? 'rgba(255,255,255,0.8)' : '#919191' }}>
        {palette.tagline}
      </div>
    </button>
  );
}
